import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { LifeBuoy } from "lucide-react";
import { KbReindexPanel } from "./KbReindexPanel";
import { KbSettingsPanel } from "./KbSettingsPanel";
import { KbSchedulerHistoryPanel } from "./KbSchedulerHistoryPanel";
import { HelpdeskAuditPanel } from "./HelpdeskAuditPanel";
import { HelpdeskAlertSettingsPanel } from "./HelpdeskAlertSettingsPanel";
import { HelpdeskAuditRetentionPanel } from "./HelpdeskAuditRetentionPanel";
import { HelpdeskFeedbackReviewPanel } from "./HelpdeskFeedbackReviewPanel";
import { HelpdeskKpiPanel } from "./HelpdeskKpiPanel";
import { HelpdeskTrendsPanel } from "./HelpdeskTrendsPanel";
import { HelpdeskEvalRunsPanel } from "./HelpdeskEvalRunsPanel";
import { HelpdeskAlertRoutesPanel } from "./HelpdeskAlertRoutesPanel";
import { HelpdeskAlertRouteAuditPanel } from "./HelpdeskAlertRouteAuditPanel";
import { HelpdeskAlertFailuresPanel } from "./HelpdeskAlertFailuresPanel";
import { HelpdeskAlertFailureChart } from "./HelpdeskAlertFailureChart";
import { HelpdeskRetryThroughputChart } from "./HelpdeskRetryThroughputChart";
import { HelpdeskAlertLockReleasePanel } from "./HelpdeskAlertLockReleasePanel";
import { HelpdeskAlertRetentionPanel } from "./HelpdeskAlertRetentionPanel";
import { HelpdeskAlertRetryAuditPanel } from "./HelpdeskAlertRetryAuditPanel";
import { HelpdeskAlertRetrySettingsPanel } from "./HelpdeskAlertRetrySettingsPanel";
import { HelpdeskRetryBatchCancelAuditPanel } from "./HelpdeskRetryBatchCancelAuditPanel";
import { HelpdeskRouteThresholdAlertsPanel } from "./HelpdeskRouteThresholdAlertsPanel";


type TicketStatus = "open" | "in_progress" | "closed";

type Ticket = {
  id: string;
  user_id: string | null;
  email: string;
  subject: string;
  message: string;
  status: TicketStatus;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
};

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  closed: "Closed",
};

export function SupportTab() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<TicketStatus | "all">("open");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("support_tickets")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setTickets((data as Ticket[]) ?? []);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel(`admin-support:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "support_tickets" },
        () => load()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (id: string, status: TicketStatus) => {
    const { error } = await supabase
      .from("support_tickets")
      .update({ status })
      .eq("id", id);
    if (error) return toast.error(error.message);
    setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
    toast.success(`Marked ${STATUS_LABEL[status].toLowerCase()}`);
  };

  const saveNotes = async (id: string, notes: string) => {
    const { error } = await supabase
      .from("support_tickets")
      .update({ admin_notes: notes })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Notes saved");
  };

  const visible = tickets.filter((t) => filter === "all" || t.status === filter);
  const counts = {
    open: tickets.filter((t) => t.status === "open").length,
    in_progress: tickets.filter((t) => t.status === "in_progress").length,
    closed: tickets.filter((t) => t.status === "closed").length,
  };

  return (
    <div className="space-y-6">
      <HelpdeskKpiPanel />
      <HelpdeskTrendsPanel />
      <HelpdeskEvalRunsPanel />
      <HelpdeskAlertRoutesPanel />
      <HelpdeskAlertFailuresPanel />
      <HelpdeskAlertRetrySettingsPanel />
      <HelpdeskRouteThresholdAlertsPanel />
      <HelpdeskAlertFailureChart />
      <HelpdeskRetryThroughputChart />
      <HelpdeskAlertLockReleasePanel />
      <HelpdeskAlertRetryAuditPanel />
      <HelpdeskRetryBatchCancelAuditPanel />
      <HelpdeskAlertRetentionPanel />
      <HelpdeskAlertRouteAuditPanel />
      <KbReindexPanel />
      <KbSettingsPanel />
      <KbSchedulerHistoryPanel />
      <HelpdeskAuditPanel />
      <HelpdeskFeedbackReviewPanel />
      <HelpdeskAlertSettingsPanel />
      <HelpdeskAuditRetentionPanel />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <LifeBuoy className="size-5 text-primary" />
          <h2 className="font-display text-xl font-bold">Support tickets</h2>
        </div>
        <div className="flex gap-2 font-mono text-xs">
          {(["open", "in_progress", "closed", "all"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded border ${
                filter === s
                  ? "border-primary text-primary bg-primary/5"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {s === "all" ? "All" : STATUS_LABEL[s]}
              {s !== "all" && (
                <span className="ml-1.5 opacity-70">{counts[s]}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading tickets…</p>
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
          <p className="text-sm text-muted-foreground">
            No {filter === "all" ? "" : STATUS_LABEL[filter as TicketStatus].toLowerCase()}{" "}
            tickets.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {visible.map((t) => (
            <TicketCard
              key={t.id}
              ticket={t}
              onStatus={(s) => setStatus(t.id, s)}
              onSaveNotes={(n) => saveNotes(t.id, n)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function TicketCard({
  ticket,
  onStatus,
  onSaveNotes,
}: {
  ticket: Ticket;
  onStatus: (s: TicketStatus) => void;
  onSaveNotes: (n: string) => void;
}) {
  const [notes, setNotes] = useState(ticket.admin_notes ?? "");
  const [open, setOpen] = useState(false);

  return (
    <li className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-semibold">{ticket.subject}</div>
          <div className="font-mono text-xs text-muted-foreground mt-1">
            {ticket.email} ·{" "}
            {new Date(ticket.created_at).toLocaleString()}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={ticket.status} onValueChange={(v) => onStatus(v as TicketStatus)}>
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? "Hide" : "Open"}
          </Button>
        </div>
      </div>

      {open && (
        <div className="mt-4 space-y-4 border-t border-border pt-4">
          <div>
            <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
              Message
            </div>
            <p className="text-sm whitespace-pre-wrap leading-relaxed">
              {ticket.message}
            </p>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
              Internal notes
            </div>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Notes visible only to admins…"
            />
            <div className="flex justify-end mt-2">
              <Button size="sm" onClick={() => onSaveNotes(notes)}>
                Save notes
              </Button>
            </div>
          </div>
          <a
            href={`mailto:${ticket.email}?subject=Re: ${encodeURIComponent(
              ticket.subject
            )}`}
            className="inline-flex text-sm text-primary hover:underline"
          >
            Reply by email →
          </a>
        </div>
      )}
    </li>
  );
}
